import { Controller } from '@hotwired/stimulus';
import AutoSave from 'stimulus/auto_save';

export default class extends Controller {
  static targets = ['field', 'count', 'message'];

  static values = {
    autoSaveUrl: String,
    autoGrow: Boolean,
    hint: String,
    messageId: String,
    debounce: { type: Number, default: 500 }
  };

  connect() {
    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.fieldTarget.name,
      debounce: this.debounceValue,
      label: this.identifier,
      element: this.element,
      onSaved: value => this.dispatch('saved', { detail: { value } }),
      onError: message => this.showError(message)
    });

    if (!this.autoGrowValue) return;

    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize);
    this.autoGrow();
  }

  disconnect() {
    this.autoSave.cancel();
    if (!this.autoGrowValue) return;

    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
  }

  handleInput() {
    this.autoGrow();
    this.updateCount();
    this.clearError();
    if (this.autoSaveUrlValue) this.autoSave.save(this.fieldTarget.value);
  }

  // Clamping is left to the field's CSS min/max height.
  autoGrow() {
    if (!this.autoGrowValue || !this.hasFieldTarget) return;
    // Hidden (a closed sheet or dialog) measures 0, which would pin the field
    // shut; `focus` re-runs this once it is on screen.
    if (this.fieldTarget.offsetParent === null) return;

    this.fieldTarget.style.height = 'auto';
    this.fieldTarget.style.height = `${this.fieldTarget.scrollHeight}px`;
  }

  // Narrowing re-wraps the text, so the measured height goes stale.
  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.autoGrow(), 100);
  }

  updateCount() {
    if (!this.hasCountTarget) return;
    const max = this.fieldTarget.getAttribute('maxlength');
    this.countTarget.textContent = `${this.fieldTarget.value.length} of ${max} characters`;
  }

  // The hint's text lives in Ui::HintComponent's `[data-hint-text]` span; the
  // icon and colour swap is CSS-driven by the root's `--error` modifier.
  showError(message) {
    this.element.classList.add('ui-plain-text-editor--error');
    this.fieldTarget.setAttribute('aria-invalid', 'true');
    if (!this.hasMessageTarget) return;

    this.setMessageText(message);
    this.messageTarget.setAttribute('role', 'alert');
    this.messageTarget.hidden = false;
    this.describeByMessage(true);
  }

  clearError() {
    if (!this.element.classList.contains('ui-plain-text-editor--error')) return;

    this.element.classList.remove('ui-plain-text-editor--error');
    this.fieldTarget.setAttribute('aria-invalid', 'false');
    if (!this.hasMessageTarget) return;

    // Server-rendered errors carry the variant class and role on the hint
    // itself; drop them so the restored hint renders as a neutral hint.
    this.messageTarget.classList.remove('ui-hint--error');
    this.messageTarget.removeAttribute('role');
    this.setMessageText(this.hintValue);
    this.messageTarget.hidden = this.hintValue === '';
    this.describeByMessage(this.hintValue !== '');
  }

  setMessageText(text) {
    const textEl = this.messageTarget.querySelector('[data-hint-text]');
    if (textEl) textEl.textContent = text;
  }

  // The message id must come and go with the visible message.
  describeByMessage(present) {
    const ids = (this.fieldTarget.getAttribute('aria-describedby') || '')
      .split(' ')
      .filter(id => id && id !== this.messageIdValue);

    if (present) ids.unshift(this.messageIdValue);

    if (ids.length) {
      this.fieldTarget.setAttribute('aria-describedby', ids.join(' '));
    } else {
      this.fieldTarget.removeAttribute('aria-describedby');
    }
  }
}
